import { Component } from "react";
import {
  useTranslation,
  withTranslation,
  WithTranslation,
} from "react-i18next";
import { History, LocationState } from "history";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { ThunkDispatch } from "redux-thunk";
import { AppActions } from "types/actions";
import { RouteComponentProps, withRouter } from "react-router-dom";
import { startUpdateUserLanguage } from "../../auth/actions";
import { Dropdown, DropdownProps, Header, Icon } from "semantic-ui-react";
import { GET_USER_INFO, UPDATE_USER_COUNTRY } from "../../../util/common";
import { EDIT_USER_INFORMATION } from "../../../graphql/mutations/editUserInformation";
import { client } from "graphql/ApolloClient";
import countryList from "constants/countryList";
import { EDIT_THERAPIST_INFORMATION } from "graphql/mutations/editTherapistInformation";
import { EDIT_COACH_INFORMATION } from "graphql/mutations/editCoachInformation";

interface CountryDropdownProps {
  path: string;
  history: History<LocationState>;
  color: string;
  handleStatus: (string, boolean) => void;
}

interface CountryDropdownState {
  country: string;
  userId: number;
  userUuid: number;
  maxWidth: string;
}

type Props = CountryDropdownProps &
  WithTranslation &
  LinkDispatchProps &
  RouteComponentProps;

class CountryDropdown extends Component<Props, CountryDropdownState> {
  constructor(props: Props) {
    super(props);
    this.state = {
      country: "",
      userId: 0,
      userUuid: 0,
      maxWidth: "120px",
    };
    this.getUserInfo();
  }

  updateMaxWidth = () => {
    if (window.innerWidth >= 768) {
      this.setState({ maxWidth: "250px" });
    } else {
      this.setState({ maxWidth: "120px" });
    }
  };

  componentDidMount() {
    window.addEventListener("resize", this.updateMaxWidth);
    this.updateMaxWidth();
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.updateMaxWidth);
  }

  componentDidUpdate(prevProps: Props, prevState: CountryDropdownState) {
    if (prevState.country !== this.state.country) {
      this.getUserInfo();
    }
  }

  updateUserOnGuide = async (userUuid, country) => {
    await client.mutate({
      mutation: EDIT_USER_INFORMATION,
      variables: {
        userUuid: userUuid,
        attribute: "Country",
        value: country,
      },
    });
  };

  handleChange = (
    event: React.SyntheticEvent<HTMLElement>,
    data: DropdownProps
  ) => {
    const country = data.value?.toString() ?? "";
    this.setState({ country: country });
    this.updateUserInfo(country);
    this.updateUserOnGuide(this.state.userUuid, country);
  };

  updateUserInfo = async (country) => {
    const response = await UPDATE_USER_COUNTRY(country);
    if (response.status) {
      console.log("success");
      this.props.handleStatus(
        this.props.t("message.account.saveSuccessful", {
          attribute: "Country / Region of Residence",
        }),
        response.status
      );
    } else {
      alert("error");
    }
  };

  getUserInfo = async () => {
    const userData = await GET_USER_INFO();
    this.setState({
      country: userData.country,
      userId: userData.id,
      userUuid: userData.uuid,
    });
  };

  getTranslatedCountryOptions = () => {
    const { t } = this.props;
    return countryList.map((country: any) => {
      const i18nKey = `utils.countryCode.${String(country.key).toUpperCase()}`;
      const translated = t(i18nKey);
      // i18next usually returns the key when missing; fallback to the raw name.
      const displayName =
        translated && translated !== i18nKey ? translated : country.text;
      return {
        ...country,
        text: displayName,
      };
    });
  };

  render = () => {
    const { t } = this.props;
    const { country, maxWidth } = this.state;
    const translatedOptions = this.getTranslatedCountryOptions();

    return (
      <Header>
        <Header.Content>
          <Dropdown
            placeholder={t("auth.login.chooseCountry")}
            fluid
            selection
            search
            value={country ? country : ""}
            onChange={this.handleChange}
            options={translatedOptions}
            style={{ maxWidth: maxWidth }}
          />
        </Header.Content>
      </Header>
    );
  };
}

interface LinkDispatchProps {
  onUpdateLanguage: (data: string) => void;
}

const mapDispatchToProps = (
  dispatch: ThunkDispatch<any, any, AppActions>
): LinkDispatchProps => ({
  onUpdateLanguage: bindActionCreators(startUpdateUserLanguage, dispatch),
});

export default withRouter(
  connect(null, mapDispatchToProps)(withTranslation()(CountryDropdown))
);
